/**
 * OptionCard — marketing-native product/option selection card.
 *
 * Consumes @owntune/theme semantic tokens directly:
 *   - Surface       → hsl(var(--card)) with subtle border
 *   - Selection     → amber ring + warm gradient tint + --shadow-gold
 *   - Text          → hsl(var(--foreground)) / hsl(var(--muted-foreground))
 *   - Accent price  → hsl(var(--primary))
 *
 * No hardcoded colors. The card looks identical to the marketing
 * site's product cards on both `.owntune-theme` (dark, default) and
 * `.owntune-theme.light` roots.
 *
 * Props mirror the internal VisualOptionCard so drop-in usage is
 * trivial:
 *   - label            (required)
 *   - description      (optional)
 *   - imageUrl         (optional)
 *   - isSelected       (bool)
 *   - isMandatory      (bool)
 *   - priceLabel       (optional string — e.g. "+€2.38/unit" or "+€120 total")
 *   - onClick          (function)
 *   - fallbackIcon     (ReactNode — shown when imageUrl missing)
 *   - dataTestid       (optional)
 */
import React from 'react';

export function OptionCard({
    label,
    description,
    imageUrl,
    isSelected = false,
    isMandatory = false,
    priceLabel,
    onClick,
    fallbackIcon = null,
    dataTestid,
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={isMandatory && isSelected}
            data-testid={dataTestid}
            data-selected={isSelected ? 'true' : 'false'}
            className="owntune-option-card"
        >
            {isMandatory && (
                <span className="owntune-option-card__mandatory">Mandatory</span>
            )}

            {isSelected && (
                <span className="owntune-option-card__check" aria-hidden="true">
                    <svg viewBox="0 0 16 16" fill="none" width="14" height="14">
                        <path
                            d="M3 8.5L6.5 12L13 5"
                            stroke="currentColor"
                            strokeWidth="2.4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </span>
            )}

            <div className="owntune-option-card__image">
                {imageUrl ? (
                    <img src={imageUrl} alt={label} />
                ) : (
                    <span className="owntune-option-card__fallback">
                        {fallbackIcon}
                    </span>
                )}
            </div>

            <div className="owntune-option-card__body">
                <h3 className="owntune-option-card__label font-display">{label}</h3>
                {description && (
                    <p className="owntune-option-card__desc">{description}</p>
                )}
                {priceLabel && (
                    <p className="owntune-option-card__price">{priceLabel}</p>
                )}
            </div>
        </button>
    );
}
